/**
 * Global maintenance / server-down screen.
 * Enterprise layout: solid dark background, clean card, status badge, countdown.
 * On expired maintenance window calls unlockApp(). Retry: one API ping; on success, unlock and reload.
 */

import { useState, useEffect } from 'react'
import { appLockStore } from '@/store/appLock.store'
import api from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api.constants'
import { settingService } from '@/services/api/platform/setting.service'
import { MaintenanceView, type MaintenanceViewData } from '@/components/maintenance/MaintenanceView'
import { UrlGenerator } from '@/utils/url-generator.util'
import { tokenManager } from '@/utils/tokenManager'

export function MaintenanceScreen() {
  const state = appLockStore.getState()
  const [retrying, setRetrying] = useState(false)
  const [runtimeData, setRuntimeData] = useState<MaintenanceViewData | null>(null)

  const isMaintenance = state.reason === 'maintenance'
  const isServerDown = state.reason === 'server-down'

  // Poll public maintenance API while locked: when admin disables maintenance, unlock and reload
  // (Users on this screen often have no WebSocket, so they never receive maintenance:disabled.)
  const MAINTENANCE_POLL_MS = 15_000
  useEffect(() => {
    if (!isMaintenance) return
    const id = setInterval(() => {
      settingService.getMaintenanceRuntime().then((data) => {
        if (data?.maintenance_mode !== true) {
          appLockStore.unlockApp()
          window.location.reload()
        }
      }).catch(() => {})
    }, MAINTENANCE_POLL_MS)
    return () => clearInterval(id)
  }, [isMaintenance])

  // Auto-recover when backend comes back after restart: poll a lightweight endpoint; on success, unlock and reload.
  // Avoids the app staying stuck on "System temporarily unavailable" after every backend edit/restart.
  const SERVER_DOWN_POLL_MS = 3_000
  useEffect(() => {
    if (!isServerDown) return
    const id = setInterval(() => {
      api.get(API_ENDPOINTS.USERS.UI_PREFERENCES, { timeout: 5000 })
        .then(() => {
          appLockStore.unlockApp()
          window.location.reload()
        })
        .catch(() => {})
    }, SERVER_DOWN_POLL_MS)
    return () => clearInterval(id)
  }, [isServerDown])

  useEffect(() => {
    if (!isMaintenance) return
    settingService.getMaintenanceRuntime().then((data) => {
      setRuntimeData({
        message: typeof data?.maintenance_message === 'string' ? data.maintenance_message : state.message ?? '',
        announcementMessage:
          typeof data?.maintenance_announcement_message === 'string'
            ? data.maintenance_announcement_message
            : '',
        expectedUptime:
          typeof data?.maintenance_expected_uptime === 'string'
            ? data.maintenance_expected_uptime
            : state.expectedUptime ?? '',
        startAt: typeof data?.maintenance_start_at === 'string' ? data.maintenance_start_at : undefined,
        endAt: typeof data?.maintenance_end_at === 'string' ? data.maintenance_end_at : undefined,
        maintenanceMode: data?.maintenance_mode === true,
        scheduleEnabled: data?.maintenance_schedule_enabled === true,
      })
    }).catch(() => {
      setRuntimeData({
        message: state.message ?? '',
        expectedUptime: state.expectedUptime ?? '',
        maintenanceMode: true,
      })
    })
  }, [isMaintenance, state.message, state.expectedUptime])

  const handleRetry = async () => {
    setRetrying(true)
    try {
      await api.get(API_ENDPOINTS.USERS.UI_PREFERENCES, { timeout: 10000 })
      appLockStore.unlockApp()
      window.location.reload()
    } catch (err: any) {
      const status = err?.response?.status
      // Token missing or expired → redirect to login so user can re-authenticate
      if (status === 401 || status === 403) {
        appLockStore.unlockApp()
        sessionStorage.setItem('logoutInProgress', 'true')
        tokenManager.clearAllAuthData()
        try {
          sessionStorage.clear()
          sessionStorage.setItem('logoutInProgress', 'true')
        } catch {
          // ignore
        }
        window.location.replace(UrlGenerator.getBaseLogoutUrl())
        return
      }
      // Other errors (network, 5xx): stay locked; user can retry again
    } finally {
      setRetrying(false)
    }
  }

  const handleExpired = () => {
    appLockStore.unlockApp()
  }

  if (isMaintenance && runtimeData) {
    return (
      <MaintenanceView
        data={runtimeData}
        primaryAction={{ label: 'Retry', onClick: handleRetry, loading: retrying }}
        onExpired={handleExpired}
        alwaysShowPrimaryAction
      />
    )
  }

  return (
    <MaintenanceView
      data={{
        message: state.message ?? undefined,
        expectedUptime: state.expectedUptime ?? undefined,
      }}
      title="System temporarily unavailable"
      subtitle={
        state.reason === 'server-down'
          ? 'Service temporarily unavailable'
          : 'System is temporarily unavailable'
      }
      primaryAction={{ label: 'Retry', onClick: handleRetry, loading: retrying }}
      alwaysShowPrimaryAction
    />
  )
}
